import React, { useState, useEffect } from 'react';
import { 
  X, AlertCircle, 
  CheckCircle, Percent, Lock, CreditCard, Shield,
  Star, Loader2
} from 'lucide-react';
import api from '../../api';
import { loadRazorpayScript } from './PaymentCheckoutModal';

const SubscriptionRenewModal = ({ 
  isOpen, 
  onClose, 
  currentPlan, 
  currentPrice,
  billingCycle: defaultBillingCycle,
  onSubmit,
  isSubmitting,
  tenantId,
  currentSubscription
}) => {
  const [billingCycle, setBillingCycle] = useState(defaultBillingCycle || 'yearly');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [, setOrderData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadRazorpayScript();
      setError(null);
      setSuccess(false);
      setIsPaymentLoading(false);
      setIsVerifying(false);
      setIsProcessing(false);
      setPaymentCompleted(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasActiveSubscription = currentPlan && currentPlan.planId;

  const getAvailableBillingCycles = () => {
    const cycles = [];
    if (currentPlan?.pricing) {
      if (currentPlan.pricing.yearly?.isActive && currentPlan.pricing.yearly?.amount > 0) {
        cycles.push({
          key: 'yearly',
          label: 'Yearly Plan',
          amount: currentPlan.pricing.yearly.amount,
          description: 'Best total value'
        });
      }
      if (currentPlan.pricing.quarterly?.isActive && currentPlan.pricing.quarterly?.amount > 0) {
        cycles.push({
          key: 'quarterly',
          label: 'Quarterly Plan',
          amount: currentPlan.pricing.quarterly.amount,
          description: 'Standard multi-month term'
        });
      }
      if (currentPlan.pricing.monthly?.isActive && currentPlan.pricing.monthly?.amount > 0) {
        cycles.push({
          key: 'monthly',
          label: 'Monthly Plan',
          amount: currentPlan.pricing.monthly.amount,
          description: 'Pay month to month'
        });
      }
    }
    if (cycles.length === 0) {
      cycles.push({
        key: 'yearly',
        label: 'Yearly Plan',
        amount: currentPrice || 0,
        description: 'Standard interval rate'
      });
    }
    return cycles;
  };

  const availableCycles = getAvailableBillingCycles();
  const selectedCycle = availableCycles.find(c => c.key === billingCycle) || availableCycles[0];

  const taxInfo = currentPlan?.tax || {};
  const hasTax = taxInfo.taxPercentage > 0;
  const taxName = taxInfo.taxName || 'GST';
  const taxPercentage = taxInfo.taxPercentage || 0;
  const isTaxInclusive = taxInfo.isTaxInclusive || false;

  const isSpecialPrice = currentSubscription?.actualPrice?.isDiscounted || 
                        currentPlan?.actualPrice?.isSpecialPrice || false;
  const specialAmount = currentSubscription?.actualPrice?.amount || 
                        currentPlan?.actualPrice?.amount || 
                        currentPrice;
  const originalAmount = currentSubscription?.actualPrice?.originalAmount || 
                        currentPlan?.actualPrice?.originalAmount || 
                        currentPrice;
  const discountReason = currentSubscription?.actualPrice?.discountReason || 
                        currentPlan?.actualPrice?.discountReason || '';

  const calculateTaxAmount = (price) => {
    if (!hasTax || price === 0) return { baseAmount: price, taxAmount: 0, totalAmount: price };
    const taxRate = taxPercentage / 100;
    if (isTaxInclusive) {
      const baseAmount = price / (1 + taxRate);
      const taxAmount = price - baseAmount;
      return { 
        baseAmount: Number(baseAmount.toFixed(2)), 
        taxAmount: Number(taxAmount.toFixed(2)), 
        totalAmount: Number(price.toFixed(2)) 
      };
    } else {
      const taxAmount = price * taxRate;
      return { 
        baseAmount: Number(price.toFixed(2)), 
        taxAmount: Number(taxAmount.toFixed(2)), 
        totalAmount: Number((price + taxAmount).toFixed(2)) 
      };
    }
  };

  const priceForTax = isSpecialPrice ? specialAmount : (selectedCycle?.amount || 0);
  const taxDetails = calculateTaxAmount(priceForTax);
  const isFreePlan = priceForTax === 0;

  const verifyPayment = async (razorpayResponse, orderData) => {
    try {
      setIsVerifying(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await api.post('/api/tenant-admin/subscription/payment/verify', {
        razorpayOrderId: razorpayResponse.razorpay_order_id,
        razorpayPaymentId: razorpayResponse.razorpay_payment_id,
        razorpaySignature: razorpayResponse.razorpay_signature,
        paymentId: orderData.paymentId,
        tenantId: tenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPaymentCompleted(true);
        setSuccess(true);
        setIsVerifying(false);
        
        if (onSubmit) {
          try {
            await onSubmit({ 
              action: 'refresh',
              planId: currentPlan?.planId,
              billingCycle: billingCycle 
            });
          } catch (refreshError) {
            console.warn('Background refresh failed:', refreshError);
          }
        }
        
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setPaymentCompleted(false);
        }, 2500);
      } else {
        setError(response.data.message || 'Payment verification failed.');
        setIsVerifying(false);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || 'Could not verify your payment.');
      setIsVerifying(false);
    }
  };

  const handlePayment = async () => {
    try {
      setIsPaymentLoading(true);
      setError(null);
      const action = hasActiveSubscription ? 'renew' : 'new';
      const token = localStorage.getItem('token');
      const response = await api.post('/api/tenant-admin/subscription/payment/create-order', {
        planId: currentPlan?.planId,
        action: action,
        tenantId: tenantId,
        billingCycle: billingCycle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const orderData = response.data.data;
        setOrderData(orderData);
        
        const razorpayOptions = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: Math.round(orderData.amount * 100),
          currency: orderData.currency || 'INR',
          name: 'Truyon',
          description: `${action === 'new' ? 'Setup' : 'Renewal'}`,
          order_id: orderData.orderId,
          theme: { color: '#4f46e5' },
          modal: {
            ondismiss: function() {
              setIsPaymentLoading(false);
            }
          },
          handler: function(response) {
            verifyPayment(response, orderData);
          }
        };

        const razorpay = new window.Razorpay(razorpayOptions);
        razorpay.open();
      } else {
        setError(response.data.message || 'Could not create payment invoice.');
        setIsPaymentLoading(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.message || 'Could not start payment process.');
      setIsPaymentLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (priceForTax > 0) {
      await handlePayment();
    } else {
      try {
        setIsProcessing(true);
        const result = await onSubmit({
          planId: currentPlan?.planId,
          billingCycle: billingCycle
        });

        if (result?.success) {
          setSuccess(true);
          setTimeout(() => {
            onClose();
            setSuccess(false);
          }, 2500);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to activate free subscription.');
        setIsProcessing(false);
      }
    }
  };

  const isLoading = isSubmitting || isPaymentLoading || isVerifying || isProcessing;
  const showLoadingOverlay = isLoading && !success && !paymentCompleted;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto shadow-2xl relative flex flex-col transition-all border border-slate-100 animate-slideUp sm:animate-scaleIn">
        
        {showLoadingOverlay && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-xs z-50 flex flex-col items-center justify-center p-6 text-center">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
            <h4 className="text-sm font-bold text-slate-800">
              {isVerifying ? 'Verifying your payment...' : isPaymentLoading ? 'Connecting to secure gateway...' : 'Updating your subscription...'}
            </h4>
            <p className="text-xs text-slate-400 max-w-[200px]">Please wait while we process your request.</p>
          </div>
        )}

        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-sm font-black text-slate-900">
              {hasActiveSubscription ? 'Renew Subscription' : 'Choose Plan'}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Plan: <span className="font-semibold text-slate-700">{currentPlan?.name || 'Standard Plan'}</span>
            </p>
          </div>
          <button 
            type="button"
            onClick={onClose} 
            disabled={isLoading || success}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 flex-1">
          
          <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-3.5 space-y-2 text-xs">
            <div className="flex justify-between items-center text-slate-500">
              <span>Selected Plan</span>
              <span className="font-bold text-slate-800">{currentPlan?.name || 'Default Plan'}</span>
            </div>
            
            <div className="flex justify-between items-center pt-2 border-t border-slate-100">
              <span className="text-slate-500">Price</span>
              {isSpecialPrice ? (
                <div className="text-right">
                  <span className="font-bold text-emerald-600">₹{specialAmount.toFixed(2)}</span>
                  <span className="text-[11px] text-slate-400 line-through ml-1.5">₹{originalAmount.toFixed(2)}</span>
                </div>
              ) : (
                <span className="font-bold text-slate-800">₹{(currentPrice || 0).toFixed(2)} / year</span>
              )}
            </div>

            {discountReason && isSpecialPrice && (
              <div className="mt-2 p-2 bg-emerald-50 text-emerald-700 rounded-lg flex items-center gap-1.5 font-medium">
                <Star className="w-3.5 h-3.5 flex-shrink-0" />
                <p className="text-[11px]">{discountReason}</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide">Select Billing Interval</label>
            <div className="space-y-2">
              {availableCycles.map((cycle) => (
                <button
                  key={cycle.key}
                  type="button"
                  onClick={() => !isLoading && !success && setBillingCycle(cycle.key)}
                  disabled={isLoading || success}
                  className={`w-full text-left p-3 rounded-xl border flex items-center justify-between transition-all ${
                    billingCycle === cycle.key
                      ? 'border-indigo-600 bg-indigo-50/50 ring-2 ring-indigo-100'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-slate-900 block">{cycle.label}</span>
                    <span className="text-[11px] text-slate-400 block">{cycle.description}</span>
                  </div>
                  <div className="text-right space-y-0.5">
                    <span className="text-xs font-black text-slate-900 block">₹{cycle.amount.toFixed(2)}</span>
                    <span className="text-[10px] text-slate-400 block uppercase font-medium">{cycle.key}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {hasTax && (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 space-y-2 text-xs">
              <div className="flex items-center gap-1.5 text-slate-700 font-bold">
                <Percent className="w-3.5 h-3.5 text-slate-400" />
                <span>Tax Details</span>
              </div>
              <div className="space-y-1.5 text-slate-500 pt-1">
                <div className="flex justify-between">
                  <span>Base price:</span>
                  <span>₹{taxDetails.baseAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{taxName} ({taxPercentage}%):</span>
                  <span>₹{taxDetails.taxAmount.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-slate-400 italic pt-1">
                  {isTaxInclusive ? '*Tax included in price' : '*Tax added at checkout'}
                </p>
              </div>
            </div>
          )}

          <div className="bg-indigo-600 text-white rounded-xl p-4 shadow-md space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium opacity-90">Total Amount</span>
              <span className="text-lg font-black">₹{taxDetails.totalAmount.toFixed(2)}</span>
            </div>
            <p className="text-[10px] opacity-75 leading-relaxed pt-1 border-t border-indigo-500/40 mt-1">
              {hasActiveSubscription 
                ? 'New subscription period will start after current one ends.' 
                : 'Your subscription starts immediately.'}
            </p>
          </div>

          {error && (
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-2 animate-fadeIn">
              <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-rose-700 leading-normal font-medium">{error}</p>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="text-xs text-rose-600 font-semibold hover:text-rose-700 mt-1"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {success && (
            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-2 animate-fadeIn">
              <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-emerald-700 leading-normal font-bold">
                  {hasActiveSubscription ? 'Subscription renewed successfully!' : 'Subscription activated successfully!'}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  Closing...
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row items-center gap-2 pt-2 border-t border-slate-50">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading && !success}
              className="w-full sm:w-1/3 py-2.5 text-center text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || success}
              className="w-full sm:w-2/3 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition shadow-sm active:scale-98 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isFreePlan ? (
                <span>Activate Free Plan</span>
              ) : (
                <>
                  <CreditCard className="w-3.5 h-3.5" />
                  <span>{hasActiveSubscription ? 'Pay & Renew' : 'Subscribe Now'}</span>
                </>
              )}
            </button>
          </div>

          {!isFreePlan && (
            <div className="flex items-center justify-center gap-3 text-[10px] text-slate-400 font-medium pt-1">
              <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-slate-300" /> Razorpay Secured</span>
              <span className="text-slate-200">|</span>
              <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-slate-300" /> 256-Bit SSL Encryption</span>
            </div>
          )}

        </form>
      </div>
    </div>
  );
};

export default SubscriptionRenewModal;